import { Plate } from '../components/Card';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getPayrollSheet, savePayrollSheet, seedPayrollRoster,
  savePayrollEmployee, dismissPayrollEmployee, restorePayrollEmployee,
} from '../api';

const GROUPS = [
  { key: 'розница', label: 'Розница', button: 'Розница', color: '#00a7dc' },
  { key: 'академия', label: 'Академия', button: 'Академия', color: '#00a7dc' },
  { key: 'ук', label: 'УК', button: 'УК', color: '#e08a00' },
  { key: 'франшиза_сопровождение', label: 'Отдел сопровождения франшизы', button: 'Сопровождение', color: '#e08a00' },
  { key: 'продажи', label: 'Отдел продаж', button: 'Продажи', color: '#0e9f6e' },
  { key: 'офис', label: 'Офис', button: 'Офис', color: '#00a7dc' },
  { key: 'управление', label: 'Руководство', button: 'Руководство', color: '#e08a00' },
];
const SCHEMES = [
  { key: 'salary', label: 'Оклад' },
  { key: 'percent', label: 'Оклад + %' },
  { key: 'piece', label: 'Сдельно' },
];
const SCHEME_LABEL = Object.fromEntries(SCHEMES.map(s => [s.key, s.label]));

function roleLine(emp) {
  if (emp.department === 'франшиза_сопровождение') return 'руководитель сопровождения франшизы';
  if (emp.department === 'продажи') return 'руководитель отдела продаж';
  if (emp.position && emp.position !== 'Офис') return emp.position;
  return '';
}

function placesWord(n) {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return 'место';
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return 'места';
  return 'мест';
}
const num = v => parseFloat(String(v ?? '').replace(/\s/g, '').replace(',', '.')) || 0;
function percentLabelText(value) {
  const raw = String(value || '').trim();
  if (!raw || /^[\d\s]+([.,]\d+)?$/.test(raw)) return '';
  return raw;
}
function percentBaseFromLabel(value) {
  const raw = String(value || '').trim().replace(/\s/g, '').replace(',', '.');
  return /^\d+(\.\d+)?$/.test(raw) ? num(raw) : 0;
}
const money = n => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round((v + Math.sign(v) * 1e-8) * 100) / 100;
};
const fmt = n => (n || n === 0) && n !== 0
  ? Math.round(n).toLocaleString('ru-RU')
  : '—';
const fmtExact = n => Number(n || 0).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function defaultPeriod() {
  const d = new Date();
  let y = d.getFullYear();
  let m = d.getMonth() + 1;
  if (d.getDate() < 15) {
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  return `${y}-${String(m).padStart(2, '0')}`;
}
const prevMonth = p => {
  const [y, m] = p.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
};
const nextMonth = p => {
  const [y, m] = p.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
};
function daysInMonth(period) {
  const [y, m] = period.split('-').map(Number);
  return new Date(y, m, 0).getDate();
}
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const MONTHS_NOM = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
function periodTitle(period) {
  const [y, m] = period.split('-').map(Number);
  return `${MONTHS_NOM[m - 1]} ${y}`;
}
function payCalendar(period) {
  const [y, m] = period.split('-').map(Number);
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return {
    advance_label: `25 ${MONTHS[m - 1]}`,
    salary_label: `10 ${MONTHS[nm - 1]}`,
    advance_short: '25',
    salary_short: '10',
  };
}

function emptyDraft(emp) {
  return {
    vacation_days: 0,
    avg_monthly: 0,
    percent_base: 0,
    lines: [],
    fines: 0,
    bonuses: 0,
    pay_cash_25: 0,
    pay_official_25: emp?.official_25 || 0,
    pay_official_10: emp?.official_10 || 0,
    pay_cash_10: 0,
    vacation_manual: 0,
    vacation_paid: 0,
    notes: '',
  };
}

const CONTRIB_FULL_LIMIT = 40639.5;

function officialTax(emp) {
  if (!emp?.is_official) {
    return { official_gross: 0, ndfl: 0, official_net: 0, contributions: 0, tax_total: 0 };
  }
  const cardNet = num(emp?.official_25) + num(emp?.official_10);
  const gross = cardNet > 0 ? cardNet / 0.87 : 0;
  const ndfl = money(gross * 0.13);
  const official_net = money(gross - ndfl);
  const contributions = gross <= CONTRIB_FULL_LIMIT
    ? money(gross * 0.30)
    : money(CONTRIB_FULL_LIMIT * 0.30 + (gross - CONTRIB_FULL_LIMIT) * 0.15);
  return {
    official_gross: money(gross),
    ndfl,
    official_net,
    contributions,
    tax_total: money(ndfl + contributions),
  };
}

function computeRow(emp, input, period) {
  const src = input || emptyDraft(emp);
  const days = daysInMonth(period);
  const vacation = Math.min(days, Math.max(0, parseInt(src.vacation_days, 10) || 0));
  const workedRaw = src.worked_days;
  const worked = workedRaw === undefined || workedRaw === null || workedRaw === ''
    ? days - vacation
    : Math.max(0, Math.min(parseInt(workedRaw, 10) || 0, days));
  const base = num(emp.base_salary);
  const rate = num(emp.percent_rate);
  const lines = [];
  if ((emp.scheme === 'salary' || emp.scheme === 'percent') && base > 0) {
    lines.push({
      label: `Оклад, ${worked} из ${days} дн.`,
      amount: money(base / days * worked),
      formula: `${fmtExact(base)} / ${days} × ${worked}`,
    });
  }
  if (emp.scheme === 'percent' && rate > 0) {
    const pct = money(rate * 100);
    const pbase = num(src.percent_base);
    lines.push({
      label: `${pct}% от «${emp.percent_label || 'база процента'}»`,
      amount: money(pbase * rate),
      formula: `${fmtExact(pbase)} × ${pct}%`,
    });
  }
  (src.lines || []).forEach(line => {
    const label = String(line.label || '').trim();
    const amount = num(line.amount);
    if (!label || !amount) return;
    lines.push({ label, amount: money(amount), formula: 'доп. строка' });
  });
  let vacationAccrued = 0;
  if (num(src.vacation_manual) > 0) {
    vacationAccrued = money(src.vacation_manual);
    lines.push({ label: 'Отпускные', amount: vacationAccrued, formula: 'сумма из ведомости' });
  } else if (vacation > 0 && num(src.avg_monthly) > 0) {
    vacationAccrued = money(num(src.avg_monthly) / 29.3 * vacation);
    lines.push({
      label: `Отпускные, ${vacation} дн.`,
      amount: vacationAccrued,
      formula: `${fmtExact(src.avg_monthly)} / 29.3 × ${vacation}`,
    });
  }
  if (num(src.bonuses) > 0) lines.push({ label: 'Премии', amount: money(src.bonuses), formula: 'премия' });
  if (num(src.fines) > 0) lines.push({ label: 'Штрафы', amount: money(-num(src.fines)), formula: 'штраф' });

  const accrued = money(lines.reduce((s, l) => s + l.amount, 0));
  const pay_cash_25 = money(src.pay_cash_25);
  const pay_cash_10 = money(src.pay_cash_10);
  const pay_official_25 = emp.is_official ? money(src.pay_official_25) : 0;
  const pay_official_10 = emp.is_official ? money(src.pay_official_10) : 0;
  const advance = money(pay_cash_25 + pay_official_25);
  const official_paid = money(pay_official_25 + pay_official_10);
  const cash_paid = money(pay_cash_25 + pay_cash_10);
  const paid = money(advance + pay_official_10 + pay_cash_10);
  const balance = money(accrued - paid);
  const vacationPaid = money(src.vacation_paid);
  const tax = officialTax(emp);
  const flags = [];
  const percentOpen = emp.scheme === 'percent' && rate > 0 && num(src.percent_base) <= 0 && worked > 0;
  const vacationOpen = vacation > 0 && num(src.avg_monthly) <= 0 && num(src.vacation_manual) <= 0;
  if (paid > 0 && accrued <= 0) flags.push({ code: 'advance_without_accrual', text: 'Аванс уже выдан, начисление за месяц ещё не закрыто' });
  else if (balance < -1 && !percentOpen && !vacationOpen) flags.push({ code: 'overpaid', text: 'Выплачено больше, чем начислено' });
  if (percentOpen) {
    flags.push({ code: 'percent_base_empty', text: 'Не внесена база процента — переменная часть равна нулю' });
  }
  if (vacationOpen) {
    flags.push({ code: 'vacation_without_average', text: 'Указаны дни отпуска, но нет средней зарплаты для отпускных' });
  }
  return {
    ...src, lines, accrued, pay_cash_25, pay_official_25, pay_official_10, pay_cash_10,
    advance, official_paid, cash_paid, paid, balance, flags, worked, days, vacation,
    vacation_accrued: vacationAccrued,
    vacation_paid: vacationPaid,
    vacation_balance: money(vacationAccrued - vacationPaid),
    ...tax,
  };
}

function draftFromRow(row) {
  return {
    vacation_days: row.vacation_days || 0,
    avg_monthly: row.avg_monthly || 0,
    percent_base: row.percent_base || 0,
    lines: (row.extra_lines || []).map(l => ({ label: l.label, amount: l.amount })),
    fines: row.fines || 0,
    bonuses: row.bonuses || 0,
    pay_cash_25: row.pay_cash_25 || 0,
    pay_official_25: row.pay_official_25 || 0,
    pay_official_10: row.pay_official_10 || 0,
    pay_cash_10: row.pay_cash_10 || 0,
    vacation_manual: row.vacation_manual || 0,
    vacation_paid: row.vacation_paid || 0,
    notes: row.notes || '',
  };
}

const btnBase = {
  height: 36, padding: '0 14px', borderRadius: 8, fontSize: 13, fontWeight: 700,
  whiteSpace: 'nowrap', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const fieldStyle = {
  width: '100%', height: 36, boxSizing: 'border-box', background: 'var(--bg)', color: 'var(--text1)',
  border: '1px solid var(--border)', borderRadius: 6, padding: '0 10px', fontSize: 13,
};

const cellInput = {
  width: '100%', height: 36, minWidth: 0, boxSizing: 'border-box',
  background: 'var(--bg)', color: 'var(--text1)', border: '1px solid var(--border)', borderRadius: 4,
  padding: '0 6px', fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit',
};

function MoneyInput({ value, onChange, color, field, placeholder }) {
  const rawRef = useRef('');
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState('');
  const shown = focused ? raw : (num(value) ? fmtExact(value) : '');
  return (
    <input
      type="text"
      inputMode="decimal"
      data-pay-field={field || 'amount'}
      placeholder={placeholder || '0'}
      value={shown}
      onFocus={() => {
        const next = num(value) ? String(value) : '';
        rawRef.current = next;
        setRaw(next);
        setFocused(true);
      }}
      onChange={e => {
        rawRef.current = e.target.value;
        setRaw(e.target.value);
      }}
      onBlur={() => {
        setFocused(false);
        const next = num(rawRef.current);
        if (next !== num(value)) onChange(next);
      }}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      style={{ ...cellInput, color: num(value) || focused ? (color || 'var(--text1)') : 'var(--text2)' }}
    />
  );
}

function PersonModal({ emp, preset, period, monthDraft, onSave, onClose }) {
  const storedBase = num(monthDraft?.percent_base);
  const [f, setF] = useState({
    name: emp?.name || '',
    position: emp?.position || preset?.position || '',
    department: emp?.department || preset?.department || 'розница',
    legal_entity: emp?.legal_entity || '',
    scheme: emp?.scheme || preset?.scheme || 'piece',
    base_salary: emp?.base_salary || 0,
    percent_rate: emp?.percent_rate ? Math.round(emp.percent_rate * 1000) / 10 : 0,
    percent_label: percentLabelText(emp?.percent_label),
    percent_base: storedBase || percentBaseFromLabel(emp?.percent_label),
    official_25: emp?.official_25 || 0,
    official_10: emp?.official_10 || 0,
    is_official: emp ? !!emp.is_official : !!preset?.is_official,
    sort_order: emp?.sort_order ?? 100,
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const [formError, setFormError] = useState('');
  const dept = GROUPS.find(g => g.key === f.department);
  const submit = () => {
    if (!String(f.name || '').trim()) { setFormError('Укажите имя'); return; }
    if (!String(f.position || '').trim()) { setFormError('Укажите должность'); return; }
    setFormError('');
    onSave({
      ...f,
      name: f.name.trim(),
      position: f.position.trim(),
      is_official: !!f.is_official,
      legal_entity: f.is_official ? f.legal_entity : '',
      official_25: f.is_official ? f.official_25 : 0,
      official_10: f.is_official ? f.official_10 : 0,
      percent_rate: f.scheme === 'percent' ? f.percent_rate / 100 : 0,
      percent_label: percentLabelText(f.percent_label),
      percent_base: f.scheme === 'percent' ? num(f.percent_base) : 0,
      base_salary: f.scheme === 'piece' ? 0 : f.base_salary,
    });
  };
  const monthAccrual = (() => {
    if (f.scheme !== 'percent') return 0;
    const previewEmp = {
      ...(emp || {}),
      scheme: 'percent',
      base_salary: num(f.base_salary),
      percent_rate: num(f.percent_rate) / 100,
      percent_label: percentLabelText(f.percent_label),
      is_official: !!f.is_official,
      official_25: f.is_official ? num(f.official_25) : 0,
      official_10: f.is_official ? num(f.official_10) : 0,
    };
    const src = { ...(monthDraft || emptyDraft(previewEmp)), percent_base: num(f.percent_base) };
    return computeRow(previewEmp, src, period || defaultPeriod()).accrued;
  })();
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, width: 520, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <h3 style={{ margin: '0 0 6px', color: 'var(--text1)', fontSize: 18 }}>{emp?.id ? 'Карточка сотрудника' : 'Новый сотрудник'}</h3>
        <p style={{ margin: '0 0 16px', color: 'var(--text2)', fontSize: 13, lineHeight: 1.45 }}>
          Оклад и процент хранятся в карточке. Сумма, от которой считается процент, записывается в открытый месяц и сразу попадает в «Начислено».
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ gridColumn: '1 / -1' }}>
            <div style={lbl}>Имя</div>
            <input style={fieldStyle} value={f.name} onChange={e => set('name', e.target.value)} />
          </label>
          <label>
            <div style={lbl}>Должность</div>
            <input style={fieldStyle} value={f.position} required placeholder="Мастер, МОП, HRBP" onChange={e => set('position', e.target.value)} />
          </label>
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: dept ? dept.color : 'var(--text1)', margin: '4px 0 10px' }}>
          Отдел: {dept ? dept.label : f.department}
        </div>
        <div style={lbl}>Оформление</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[[true, 'Официально', '#00a7dc'], [false, 'Неофициально', '#00a7dc']].map(([key, label, color]) => (
            <button key={label} type="button" onClick={() => set('is_official', key)} style={{
              flex: '1 1 0', width: 0, height: 36, minHeight: 36, padding: '0 12px', borderRadius: 8,
              fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', boxSizing: 'border-box',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
              border: `1px solid ${f.is_official === key ? color : 'var(--border)'}`,
              background: f.is_official === key ? color + '26' : 'transparent',
              color: f.is_official === key ? color : 'var(--text2)',
            }}>{label}</button>
          ))}
        </div>
        <div style={lbl}>Группа</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
          {GROUPS.filter(g => g.button).map(g => (
            <button key={g.key} type="button" onClick={() => set('department', g.key)} style={{
              height: 36, minHeight: 36, padding: '0 8px', borderRadius: 8, fontSize: 13, fontWeight: 700,
              whiteSpace: 'nowrap', cursor: 'pointer', boxSizing: 'border-box', width: '100%',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
              border: `1px solid ${f.department === g.key ? g.color : 'var(--border)'}`,
              background: f.department === g.key ? g.color + '22' : 'transparent',
              color: f.department === g.key ? g.color : 'var(--text2)',
            }}>{g.button}</button>
          ))}
        </div>
        <div style={lbl}>Как считается заработок</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {SCHEMES.map(s => (
            <button key={s.key} type="button" onClick={() => set('scheme', s.key)} style={{
              ...btnBase, flex: 1, border: `1px solid ${f.scheme === s.key ? '#00a7dc' : 'var(--border)'}`,
              background: f.scheme === s.key ? 'rgba(0,167,220,.15)' : 'transparent',
              color: f.scheme === s.key ? '#0089b6' : 'var(--text2)',
            }}>{s.label}</button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {f.scheme !== 'piece' && (
            <label>
              <div style={lbl}>Оклад, ₽</div>
              <input style={fieldStyle} type="number" value={f.base_salary} onChange={e => set('base_salary', num(e.target.value))} />
            </label>
          )}
          {f.scheme === 'percent' && (
            <>
              <label>
                <div style={lbl}>Процент</div>
                <input style={fieldStyle} type="number" value={f.percent_rate} onChange={e => set('percent_rate', num(e.target.value))} />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>
                <div style={lbl}>От какой суммы процент</div>
                <input style={fieldStyle} type="number" value={f.percent_base || ''} onChange={e => set('percent_base', num(e.target.value))} />
              </label>
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 36, padding: '0 10px', borderRadius: 8, background: 'rgba(0,167,220,.08)', color: 'var(--text1)', fontSize: 13 }}>
                <span>Начислено за этот месяц</span>
                <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtExact(monthAccrual)}</b>
              </div>
            </>
          )}
          {f.is_official && (
            <>
              <label style={{ gridColumn: '1 / -1' }}>
                <div style={lbl}>Где оформлен</div>
                <input style={fieldStyle} value={f.legal_entity} placeholder="ИП Бобров Илья / ООО" onChange={e => set('legal_entity', e.target.value)} />
              </label>
              <label>
                <div style={lbl}>На руки 25-го, ₽</div>
                <input style={fieldStyle} type="number" value={f.official_25} onChange={e => set('official_25', num(e.target.value))} />
              </label>
              <label>
                <div style={lbl}>На руки 10-го, ₽</div>
                <input style={fieldStyle} type="number" value={f.official_10} onChange={e => set('official_10', num(e.target.value))} />
              </label>
            </>
          )}
        </div>
        {f.is_official && (() => {
          const tax = officialTax({ ...f, is_official: true });
          return (
            <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--bg)', color: 'var(--text2)', fontSize: 12, lineHeight: 1.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Начисление до НДФЛ</span><b style={{ color: 'var(--text1)' }}>{fmtExact(tax.official_gross)}</b></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>НДФЛ 13%</span><b style={{ color: 'var(--text1)' }}>{fmtExact(tax.ndfl)}</b></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Взносы</span><b style={{ color: 'var(--text1)' }}>{fmtExact(tax.contributions)}</b></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Налоги всего</span><b style={{ color: 'var(--text1)' }}>{fmtExact(tax.tax_total)}</b></div>
            </div>
          );
        })()}
        {formError && <div style={{ color: '#e04848', fontSize: 14, marginBottom: 8 }}>{formError}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={submit} style={{ ...btnBase, flex: 1, background: '#00a7dc', color: '#fff', border: 'none' }}>Сохранить карточку</button>
          <button type="button" onClick={onClose} style={{ ...btnBase, background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)' }}>Отмена</button>
        </div>
      </div>
    </div>
  );
}

const lbl = { fontSize: 13, color: 'var(--text1)', margin: '8px 0 4px', fontWeight: 700 };

export default function PayrollEditor() {
  const [period, setPeriod] = useState(defaultPeriod);
  const [people, setPeople] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [history, setHistory] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState(null);
  const [archiveId, setArchiveId] = useState(null);
  const [modal, setModal] = useState(null);
  const [error, setError] = useState('');

  const applySheet = useCallback((data) => {
    const rows = data.rows || [];
    const active = rows.filter(row => row.is_active !== false);
    const archivedFromRows = rows.filter(row => row.is_active === false);
    setPeople(active);
    const next = {};
    active.forEach(row => { next[row.id] = draftFromRow(row); });
    setDrafts(next);
    setHistory(data.history || []);
    const byId = new Map();
    (data.dismissed || []).forEach(emp => byId.set(emp.id, emp));
    archivedFromRows.forEach(emp => byId.set(emp.id, { ...(byId.get(emp.id) || {}), ...emp }));
    setDismissed(Array.from(byId.values()));
    setOpenId(null);
    setArchiveId(null);
    setDirty(false);
  }, []);

  const load = useCallback(async (p) => {
    setLoading(true);
    setError('');
    try {
      applySheet(await getPayrollSheet(p));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Не удалось открыть ведомость');
    } finally {
      setLoading(false);
    }
  }, [applySheet]);

  useEffect(() => { load(period); }, [period, load]);

  const cal = payCalendar(period);
  const view = useMemo(() => people.map(emp => ({
    emp,
    calc: computeRow(emp, drafts[emp.id] || emptyDraft(emp), period),
  })), [people, drafts, period]);

  const totals = useMemo(() => {
    const blank = () => ({
      people: 0, accrued: 0, paid: 0, owe: 0, advances: 0,
      ndfl: 0, contributions: 0, tax_total: 0, official_gross: 0, official_net: 0,
      vacation_accrued: 0, vacation_paid: 0,
    });
    const add = (bucket, calc, withTax) => {
      bucket.people += 1;
      bucket.accrued += num(calc.accrued);
      bucket.paid += num(calc.paid);
      bucket.vacation_accrued += num(calc.vacation_accrued);
      bucket.vacation_paid += num(calc.vacation_paid);
      if (calc.balance > 0) bucket.owe += calc.balance;
      if (num(calc.paid) > 0 && num(calc.accrued) <= 0) bucket.advances += num(calc.paid);
      if (withTax) {
        bucket.ndfl += num(calc.ndfl);
        bucket.contributions += num(calc.contributions);
        bucket.tax_total += num(calc.tax_total);
        bucket.official_gross += num(calc.official_gross);
        bucket.official_net += num(calc.official_net);
      }
    };
    const all = blank();
    const official = blank();
    const unofficial = blank();
    const by = {};
    view.forEach(({ emp, calc }) => {
      add(all, calc, !!emp.is_official);
      add(emp.is_official ? official : unofficial, calc, !!emp.is_official);
      const g = by[emp.department] || { accrued: 0, paid: 0, owe: 0, people: 0 };
      g.people += 1;
      g.accrued += num(calc.accrued);
      g.paid += num(calc.paid);
      if (calc.balance > 0) g.owe += calc.balance;
      by[emp.department] = g;
    });
    const round = bucket => {
      Object.keys(bucket).forEach(k => { if (k !== 'people') bucket[k] = money(bucket[k]); });
      return bucket;
    };
    Object.values(by).forEach(g => {
      g.accrued = money(g.accrued);
      g.paid = money(g.paid);
      g.owe = money(g.owe);
    });
    return { all: round(all), official: round(official), unofficial: round(unofficial), by };
  }, [view]);

  const flags = view.flatMap(({ emp, calc }) => calc.flags.map(f => ({ ...f, name: emp.name })));

  const patchDraft = (id, patch) => {
    setDrafts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setStatus('');
    try {
      const data = await savePayrollSheet({
        period,
        rows: people.map(emp => ({ employee_id: emp.id, ...(drafts[emp.id] || emptyDraft(emp)) })),
      });
      applySheet(data);
      setStatus('saved');
    } catch (e) {
      setStatus(e?.response?.data?.detail || 'Не сохранилось');
    } finally {
      setSaving(false);
    }
  };

  const seed = async () => {
    if (people.length && !window.confirm('Заполнить из ведомости тех, кого ещё нет, и карточки, которые ещё не настраивали? Уже настроенных людей это не изменит.')) return;
    setSaving(true);
    try {
      await seedPayrollRoster();
      await load('2026-09');
      setPeriod('2026-09');
      setStatus('seeded');
    } catch (e) {
      setStatus(e?.response?.data?.detail || 'Не удалось собрать состав');
    } finally {
      setSaving(false);
    }
  };

  const savePerson = async (form) => {
    const { percent_base, ...card } = form;
    const saved = await savePayrollEmployee(modal?.id || null, card);
    const id = saved?.id || modal?.id;
    if (id && form.scheme === 'percent') {
      const existing = drafts[id] || (modal?.id ? drafts[modal.id] : null) || emptyDraft(saved || card);
      await savePayrollSheet({
        period,
        rows: [{
          employee_id: id,
          vacation_days: existing.vacation_days || 0,
          avg_monthly: existing.avg_monthly || 0,
          percent_base: num(percent_base),
          lines: existing.lines || [],
          fines: existing.fines || 0,
          bonuses: existing.bonuses || 0,
          pay_cash_25: existing.pay_cash_25 || 0,
          pay_official_25: existing.pay_official_25 || 0,
          pay_official_10: existing.pay_official_10 || 0,
          pay_cash_10: existing.pay_cash_10 || 0,
          vacation_manual: existing.vacation_manual || 0,
          vacation_paid: existing.vacation_paid || 0,
          notes: existing.notes || '',
        }],
      });
    }
    setModal(null);
    await load(period);
  };

  const removePerson = async (emp) => {
    if (!window.confirm(`Уволить ${emp.name}? Он пропадёт из новых месяцев. История начислений сохранится.`)) return;
    await dismissPayrollEmployee(emp.id);
    setOpenId(null);
    await load(period);
  };

  const restorePerson = async (emp) => {
    await restorePayrollEmployee(emp.id);
    await load(period);
  };

  return (
    <div style={{ color: 'var(--text1)' }}>
      <div className="kp-hero" style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ marginRight: 8 }}>
          <Plate>1С · зарплата</Plate>
          <h1 style={{ margin: 0 }}>Зарплата</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '0 6px', height: 36 }}>
          <button type="button" onClick={() => setPeriod(prevMonth(period))} style={navBtn}>‹</button>
          <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
            style={{ background: 'transparent', color: 'var(--text1)', border: 'none', fontWeight: 700, fontSize: 14 }} />
          <button type="button" onClick={() => setPeriod(nextMonth(period))} style={navBtn}>›</button>
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={seed} style={{ ...btnBase, background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)' }}>Состав из ведомости</button>
        <button type="button" onClick={save} disabled={saving || !dirty} style={{
          ...btnBase, border: 'none', minWidth: 148,
          background: dirty ? '#00a7dc' : '#0089b6', color: dirty ? '#fff' : 'var(--text2)',
          cursor: dirty ? 'pointer' : 'default',
        }}>{saving ? 'Сохраняю…' : dirty ? 'Сохранить месяц' : 'Сохранено'}</button>
      </div>

      <div style={{ background: 'rgba(224,138,0,0.12)', border: '1px solid var(--border)', borderRadius: 14, padding: '12px 16px', marginBottom: 14 }}>
        <div style={{ fontWeight: 800 }}>Начисления можно внести за 1–2 дня до выплаты</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>
          До 25-го и до 10-го Натали заполняет начисления и сохраняет месяц. Сразу видно, сколько надо выплатить (колонка «К выдаче»), и предварительный P&L в разделе «ДДС и P&L» обновляется. В движение денег сумма попадёт только в дату выплаты: 25-го аванс, 10-го остаток.
        </div>
      </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(180px, 1fr))', gap: 8, marginBottom: 14 }}>
        {[
          ['Официально', String(totals.official.people), '#00a7dc'],
          ['Неофициально', String(totals.unofficial.people), '#00a7dc'],
          ['ФОТ', fmtExact(totals.all.accrued), '#0e9f6e'],
          ['Выплачено', fmtExact(totals.all.paid), 'var(--text1)'],
          ['Осталось доплатить', fmtExact(totals.all.owe), '#e08a00'],
          ['Аванс без начисления', fmtExact(totals.all.advances), '#e04848'],
          ['НДФЛ', fmtExact(totals.official.ndfl), '#e08a00'],
          ['Взносы', fmtExact(totals.official.contributions), '#e08a00'],
          ['Отпускные начислено', fmtExact(totals.all.vacation_accrued), '#0089b6'],
          ['Отпускные выплачено', fmtExact(totals.all.vacation_paid), '#0089b6'],
        ].map(([title, value, color]) => (
          <div key={title} style={metricCard}>
            <div style={{ fontSize: 11, color, fontWeight: 800, letterSpacing: .3, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{title}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text1)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</div>
          </div>
        ))}
      </div>

      {error && <div style={banner('#e04848')}>{error}</div>}
      {status && status !== 'saved' && status !== 'seeded' && <div style={banner('#e04848')}>{String(status)}</div>}
      {flags.length > 0 && (
        <details style={{ ...banner('#e08a00'), marginBottom: 12 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{flags.length} {placesWord(flags.length)}, где месяц ещё не закрыт</summary>
          <div style={{ marginTop: 6 }}>
            {flags.slice(0, 8).map((f, i) => <div key={i}>{f.name}: {f.text}</div>)}
            {flags.length > 8 && <div>и ещё {flags.length - 8}</div>}
          </div>
        </details>
      )}

      {loading ? <div style={{ padding: 40, color: 'var(--text2)', fontSize: 15 }}>Загружаю ведомость…</div> : (
        <>
        {people.length === 0 && (
        <div style={{ padding: 28, background: 'var(--bg)', border: '1px solid var(--bg3)', borderRadius: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>В реестре пока никого нет</div>
          <div style={{ color: 'var(--text1)', fontSize: 15, marginBottom: 14, maxWidth: 640 }}>
            Можно завести состав из зарплатной ведомости или добавить человека в нужный отдел.
          </div>
          <button type="button" onClick={seed} style={{ ...btnBase, background: '#00a7dc', color: '#fff', border: 'none' }}>Собрать состав из ведомости</button>
        </div>
        )}
        {GROUPS.map(group => {
          const rows = view.filter(v => (v.emp.department || 'офис') === group.key);
          const g = totals.by[group.key] || { accrued: 0, paid: 0, owe: 0, people: 0 };
          return (
            <section key={group.key} data-dept={group.key} style={{ marginBottom: 12, background: 'var(--bg)', border: '1px solid var(--bg3)', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 52, padding: '8px 12px', borderBottom: `2px solid ${group.color}`, boxSizing: 'border-box', flexWrap: 'wrap' }}>
                <b style={{ color: group.color, fontSize: 16, whiteSpace: 'nowrap' }}>{group.label}</b>
                <span style={{ color: 'var(--text2)', fontSize: 14, whiteSpace: 'nowrap' }}>{g.people} чел.</span>
                <button type="button" data-add-dept={group.key} onClick={() => setModal({ department: group.key, scheme: 'piece', is_official: false, position: '' })} style={{ ...btnBase, background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)' }}>+ Добавить сотрудника</button>
                <span style={{ marginLeft: 'auto', fontSize: 14, color: 'var(--text1)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  начислено {fmtExact(g.accrued)} · выплачено {fmtExact(g.paid)} · осталось {fmtExact(g.owe)}
                </span>
              </div>
              {rows.length === 0 && (
                <div style={{ padding: '14px 16px', color: 'var(--text1)', fontSize: 15 }}>В отделе пока никого.</div>
              )}
              {[true, false].map(official => {
                const part = rows.filter(v => !!v.emp.is_official === official);
                if (!part.length) return null;
                const span = official ? 11 : 9;
                return (
                  <div key={official ? 'off' : 'unoff'}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: official ? '28%' : '34%' }} />
                      <col span={official ? 9 : 7} />
                      <col style={{ width: 104 }} />
                    </colgroup>
                    <thead>
                      <tr style={{ height: 36, color: 'var(--text1)', fontSize: 13 }}>
                        <th style={thL}>{official ? 'Официально' : 'Неофициально'}</th>
                        <th style={th}>Начислено</th>
                        <th style={th}>Отпуск</th>
                        <th style={th}>Отпуск выпл.</th>
                        <th style={th}>Нал {cal.advance_short}</th>
                        {official && <th style={th}>Карта {cal.advance_short}</th>}
                        {official && <th style={th}>Карта {cal.salary_short}</th>}
                        <th style={th}>Нал {cal.salary_short}</th>
                        <th style={th}>Выплачено</th>
                        <th style={th}>К выдаче</th>
                        <th style={{ width: 96, padding: 0 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {part.map(({ emp, calc }) => (
                        <React.Fragment key={emp.id}>
                          <tr style={{ borderTop: '1px solid var(--bg3)', background: openId === emp.id ? 'var(--bg)' : 'transparent' }}>
                            <td style={{ padding: '6px 12px', verticalAlign: 'middle', overflow: 'hidden' }}>
                              <button type="button" onClick={() => setOpenId(openId === emp.id ? null : emp.id)} style={nameBtn}>
                                {emp.name}
                              </button>
                              <div style={{ fontSize: 13, lineHeight: '18px', color: official ? '#0089b6' : 'var(--text2)', whiteSpace: 'nowrap' }}>
                                {roleLine(emp) ? `${roleLine(emp)} · ` : ''}
                                {official ? 'Официально' : 'Неофициально'}
                              </div>
                            </td>
                            <td style={{ ...td, color: '#0089b6', fontWeight: 700 }}>{fmtExact(calc.accrued)}</td>
                            <td style={{ verticalAlign: 'middle' }}><MoneyInput field="vacation_manual" value={calc.vacation_accrued} onChange={v => patchDraft(emp.id, { vacation_manual: v })} /></td>
                            <td style={{ verticalAlign: 'middle' }}><MoneyInput field="vacation_paid" value={calc.vacation_paid} onChange={v => patchDraft(emp.id, { vacation_paid: v })} color="#0089b6" /></td>
                            <td style={{ verticalAlign: 'middle' }}><MoneyInput field="pay_cash_25" value={calc.pay_cash_25} onChange={v => patchDraft(emp.id, { pay_cash_25: v })} /></td>
                            {official && <td style={{ verticalAlign: 'middle' }}><MoneyInput field="pay_official_25" value={calc.pay_official_25} onChange={v => patchDraft(emp.id, { pay_official_25: v })} /></td>}
                            {official && <td style={{ verticalAlign: 'middle' }}><MoneyInput field="pay_official_10" value={calc.pay_official_10} onChange={v => patchDraft(emp.id, { pay_official_10: v })} /></td>}
                            <td style={{ verticalAlign: 'middle' }}><MoneyInput field="pay_cash_10" value={calc.pay_cash_10} onChange={v => patchDraft(emp.id, { pay_cash_10: v })} /></td>
                            <td style={{ ...td, color: '#0e9f6e' }}>{fmtExact(calc.paid)}</td>
                            <td style={{ ...td, color: calc.balance > 1 ? '#e08a00' : calc.balance < -1 ? '#e04848' : 'var(--text2)', fontWeight: 700 }}>
                              {fmtExact(calc.balance)}
                            </td>
                            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'middle' }}>
                              <button type="button" onClick={() => setOpenId(openId === emp.id ? null : emp.id)} style={cardBtn}>
                                {openId === emp.id ? 'Скрыть' : 'Расчёт'}
                              </button>
                            </td>
                          </tr>
                          {openId === emp.id && (
                            <tr>
                              <td colSpan={span} style={{ padding: '4px 12px 14px', background: 'var(--bg)' }}>
                                <Detail emp={emp} calc={calc} draft={drafts[emp.id]} cal={cal}
                                  onChange={patch => patchDraft(emp.id, patch)}
                                  onEdit={() => setModal(emp)}
                                  onDismiss={() => removePerson(emp)}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  </div>
                );
              })}
            </section>
          );
        })}
        </>
      )}

      <details style={{ marginTop: 18, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 800, fontSize: 16, color: 'var(--text1)' }}>Архив уволенных · {dismissed.length}</summary>
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {dismissed.length === 0 && <div style={{ color: 'var(--text1)', fontSize: 15 }}>Пока никого.</div>}
          {dismissed.map(emp => {
            const opened = archiveId === emp.id;
            const calc = emp.accrued !== undefined ? emp : computeRow(emp, emptyDraft(emp), period);
            const when = emp.dismissed_at ? String(emp.dismissed_at).slice(0, 10).split('-').reverse().join('.') : 'дата не указана';
            const group = GROUPS.find(g => g.key === emp.department);
            return (
              <div key={emp.id} style={{ borderTop: '1px solid var(--bg3)', paddingTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text1)' }}>{emp.name}</div>
                    <div style={{ fontSize: 14, color: 'var(--text1)' }}>{group ? group.label : emp.department}{roleLine(emp) ? ` · ${roleLine(emp)}` : ''} · уволен {when}</div>
                  </div>
                  <button type="button" onClick={() => setArchiveId(opened ? null : emp.id)} style={cardBtn}>{opened ? 'Скрыть' : 'Открыть'}</button>
                  <button type="button" onClick={() => restorePerson(emp)} style={cardBtn}>Вернуть</button>
                </div>
                {opened && (
                  <div style={{ marginTop: 8 }}>
                    <Terms emp={emp} calc={calc} cal={cal} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </details>

      {history.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>Сохранённые месяцы</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {history.map(h => (
              <button key={h.period} type="button" onClick={() => setPeriod(h.period)} style={{
                ...btnBase,
                background: h.period === period ? '#0089b6' : 'var(--bg)',
                border: '1px solid var(--bg3)', color: 'var(--text1)',
              }}>
                {periodTitle(h.period)} · {fmt(h.accrued)}
              </button>
            ))}
          </div>
        </div>
      )}

      {modal && (
        <PersonModal
          emp={modal.id ? modal : null}
          preset={modal.id ? null : modal}
          period={period}
          monthDraft={modal.id ? (drafts[modal.id] || draftFromRow(modal)) : null}
          onSave={savePerson}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function Terms({ emp, calc, cal }) {
  const group = GROUPS.find(g => g.key === emp.department);
  const pct = money((emp.percent_rate || 0) * 100);
  let scheme = 'Сдельно: сумма складывается из строк месяца';
  if (emp.scheme === 'salary') scheme = `Оклад ${fmtExact(emp.base_salary)} ₽ в месяц`;
  if (emp.scheme === 'percent') {
    scheme = `Оклад ${fmtExact(emp.base_salary)} ₽ и ${pct}% от «${emp.percent_label || 'базы'}». База этого месяца ${fmtExact(calc.percent_base)} ₽`;
  }
  const vacation = num(calc.vacation_accrued) > 0 || num(calc.vacation_paid) > 0 || num(calc.vacation_days) > 0;
  const row = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span>{label}</span><b style={{ color: 'var(--text1)', fontVariantNumeric: 'tabular-nums' }}>{value}</b>
    </div>
  );
  return (
    <div style={{ padding: '14px 16px', borderRadius: 8, background: 'var(--bg)', fontSize: 15, lineHeight: 1.6, color: 'var(--text1)' }}>
      <div style={{ fontWeight: 800, color: 'var(--text1)', marginBottom: 6 }}>Условия {emp.name}</div>
      {row('Где работает', `${group ? group.label : emp.department}${roleLine(emp) ? `, ${roleLine(emp)}` : ''}`)}
      {row('Оформление', emp.is_official ? 'Официально' : 'Неофициально')}
      {emp.legal_entity ? row('Юрлицо', emp.legal_entity) : null}
      {row('Схема', SCHEME_LABEL[emp.scheme] || scheme)}
      {row('Как считаем', scheme)}
      {row(`Наличные ${cal.advance_label}`, fmtExact(calc.pay_cash_25))}
      {emp.is_official && row(`На карту ${cal.advance_label}`, fmtExact(calc.pay_official_25 || emp.official_25))}
      {row(`Наличные ${cal.salary_label}`, fmtExact(calc.pay_cash_10))}
      {emp.is_official && row(`На карту ${cal.salary_label}`, fmtExact(calc.pay_official_10 || emp.official_10))}
      {emp.is_official && (
        <>
          {row('Начисление до НДФЛ', fmtExact(calc.official_gross))}
          {row('НДФЛ 13%', fmtExact(calc.ndfl))}
          {row('Взносы', fmtExact(calc.contributions))}
          {row('На карту после налога', fmtExact(calc.official_net))}
        </>
      )}
      {vacation && row('Отпуск', `${calc.vacation_days || 0} дн., начислено ${fmtExact(calc.vacation_accrued)}, выплачено ${fmtExact(calc.vacation_paid)}`)}
      {row('Этот месяц начислено', fmtExact(calc.accrued))}
      {row('Этот месяц выплачено', fmtExact(calc.paid))}
      {row('Остаток', fmtExact(calc.balance))}
    </div>
  );
}

function Detail({ emp, calc, draft, cal, onChange, onEdit, onDismiss }) {
  const lines = draft.lines || [];
  const setLine = (i, patch) => {
    const next = lines.map((l, idx) => idx === i ? { ...l, ...patch } : l);
    onChange({ lines: next });
  };
  const rest = money(calc.accrued - calc.pay_cash_25 - calc.pay_official_25 - calc.pay_official_10 - (emp.official_10 && !calc.pay_official_10 ? emp.official_10 : 0));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr .8fr', gap: 20, padding: '8px 4px 4px' }}>
      <div>
        <Terms emp={emp} calc={calc} cal={cal} />
        <div style={{ fontSize: 12, color: 'var(--text2)', margin: '12px 0 6px' }}>Из чего сложилось начисление</div>
        {calc.lines.length === 0 && <div style={{ color: 'var(--text2)', fontSize: 13 }}>Пока пусто. Для сдельной оплаты добавьте строки: точка, бот, обучение.</div>}
        {calc.lines.map((line, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '3px 0', borderBottom: '1px solid var(--bg3)' }}>
            <span>{line.label} <span style={{ color: 'var(--text2)' }}>{line.formula}</span></span>
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtExact(line.amount)}</b>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontWeight: 800 }}>
          <span>Итого начислено</span><span>{fmtExact(calc.accrued)}</span>
        </div>
        {calc.flags.map(f => <div key={f.code} style={{ color: '#e08a00', fontSize: 12, marginTop: 6 }}>{f.text}</div>)}
        {emp.is_official && (
          <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--bg)', fontSize: 12, lineHeight: 1.55 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span>Белое начисление</span><b>{fmtExact(calc.official_gross)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span>НДФЛ 13%</span><b>{fmtExact(calc.ndfl)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span>Взносы</span><b>{fmtExact(calc.contributions)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span>Налоги всего</span><b>{fmtExact(calc.tax_total)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span>На руки</span><b>{fmtExact(calc.official_net)}</b></div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {emp.is_official && emp.official_10 > 0 && (
            <button type="button" style={cardBtn} onClick={() => onChange({ pay_official_10: emp.official_10 })}>
              На руки {fmtExact(emp.official_10)} на 10-е
            </button>
          )}
          <button type="button" style={cardBtn} onClick={() => onChange({
            pay_cash_10: Math.max(0, money(calc.accrued - calc.pay_cash_25 - calc.pay_official_25 - calc.pay_official_10)),
          })}>Остаток в наличные 10-го</button>
          <button type="button" style={cardBtn} onClick={onEdit}>Карточка</button>
          <button type="button" onClick={onDismiss} style={{ ...cardBtn, color: '#e04848', background: '#e04848', border: '1px solid #e04848' }}>Уволить</button>
        </div>
        {emp.is_official && rest > 1 && emp.official_10 > 0 && !calc.pay_official_10 && (
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>
            Если подставить официальную часть 10-го, наличными останется около {fmt(Math.max(0, rest))}.
          </div>
        )}
      </div>
      <div>
        {emp.scheme === 'percent' && (
          <label>
            <div style={lbl}>База «{emp.percent_label || 'процента'}», ₽</div>
            <input style={fieldStyle} type="number" value={draft.percent_base || ''} onChange={e => onChange({ percent_base: num(e.target.value) })} />
          </label>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <label>
            <div style={lbl}>Дней отпуска</div>
            <input style={fieldStyle} type="number" value={draft.vacation_days || ''} onChange={e => onChange({ vacation_days: parseInt(e.target.value, 10) || 0 })} />
          </label>
          <label>
            <div style={lbl}>Средняя зарплата для отпуска</div>
            <input style={fieldStyle} type="number" value={draft.avg_monthly || ''} onChange={e => onChange({ avg_monthly: num(e.target.value) })} />
          </label>
          <label>
            <div style={lbl}>Отпускные суммой</div>
            <input style={fieldStyle} type="number" value={draft.vacation_manual || ''} onChange={e => onChange({ vacation_manual: num(e.target.value) })} />
          </label>
          <label>
            <div style={lbl}>Отпускные выплачено</div>
            <input style={fieldStyle} type="number" value={draft.vacation_paid || ''} onChange={e => onChange({ vacation_paid: num(e.target.value) })} />
          </label>
          <label>
            <div style={lbl}>Штраф</div>
            <input style={fieldStyle} type="number" value={draft.fines || ''} onChange={e => onChange({ fines: num(e.target.value) })} />
          </label>
          <label>
            <div style={lbl}>Премия</div>
            <input style={fieldStyle} type="number" value={draft.bonuses || ''} onChange={e => onChange({ bonuses: num(e.target.value) })} />
          </label>
        </div>
        <div style={lbl}>Дополнительные строки</div>
        {lines.map((line, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input style={{ ...fieldStyle, flex: 1 }} value={line.label} placeholder="Точка, бот, обучение"
              onChange={e => setLine(i, { label: e.target.value })} />
            <input style={{ ...fieldStyle, width: 110 }} type="number" value={line.amount}
              onChange={e => setLine(i, { amount: num(e.target.value) })} />
            <button type="button" style={tinyBtn} onClick={() => onChange({ lines: lines.filter((_, idx) => idx !== i) })}>×</button>
          </div>
        ))}
        <button type="button" style={tinyBtn} onClick={() => onChange({ lines: [...lines, { label: '', amount: 0 }] })}>Добавить строку</button>
        <label>
          <div style={lbl}>Заметка</div>
          <input style={fieldStyle} value={draft.notes || ''} onChange={e => onChange({ notes: e.target.value })} />
        </label>
      </div>
    </div>
  );
}

const th = { textAlign: 'right', height: 36, padding: '0 8px', fontWeight: 700, fontSize: 13, color: 'var(--text1)', whiteSpace: 'nowrap', verticalAlign: 'middle' };
const thL = { textAlign: 'left', height: 36, padding: '0 12px', fontWeight: 700, fontSize: 13, color: 'var(--text1)', whiteSpace: 'nowrap', verticalAlign: 'middle' };
const td = { textAlign: 'right', padding: '0 8px', fontSize: 13, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle' };
const navBtn = { background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', fontSize: 18, height: 36, width: 28 };
const nameBtn = {
  background: 'none', border: 'none', padding: 0, margin: 0, color: '#0089b6',
  fontWeight: 700, fontSize: 15, cursor: 'pointer', whiteSpace: 'nowrap',
  textDecoration: 'underline', textUnderlineOffset: '3px', fontFamily: 'inherit',
};
const cardBtn = {
  height: 36, minHeight: 36, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg3)', color: 'var(--text1)', cursor: 'pointer', fontSize: 13, fontWeight: 700,
  whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  boxSizing: 'border-box', lineHeight: 1, fontFamily: 'inherit',
};
const tinyBtn = {
  height: 36, minHeight: 36, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg3)',
  color: 'var(--text1)', cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', fontFamily: 'inherit',
};
const banner = (bg) => ({ background: `${bg}1f`, border: `1px solid ${bg}55`, borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 10, lineHeight: 1.45 });
const metricCard = {
  background: 'var(--bg)', border: '1px solid var(--bg3)', borderRadius: 10, padding: '12px 14px',
  height: 72, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0,
};
